"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REWARDS_NUMBER_ALPHABET = exports.CUSTOMER_ACCOUNT_SECTIONS = exports.CUSTOMER_ACCOUNT_PRESETS = exports.CustomerAccountSettingsSchema = exports.CustomerAccountSectionIdSchema = exports.CUSTOMER_ACCOUNT_SECTION_IDS = exports.CustomerAccountsPresetSchema = exports.CustomerAccountsModeSchema = exports.CustomerAccountSourceSchema = exports.CustomerOnlineAccessSchema = void 0;
exports.deriveOnlineAccess = deriveOnlineAccess;
exports.defaultCustomerAccountSettings = defaultCustomerAccountSettings;
exports.resolveCustomerAccountSections = resolveCustomerAccountSections;
exports.customerAccountSectionsForLine = customerAccountSectionsForLine;
exports.generateRewardsNumber = generateRewardsNumber;
exports.normalizeRewardsNumber = normalizeRewardsNumber;
/**
 * Customer Accounts — one customer account for every business type, whose
 * sections switch on per package. Merchant page: platform-app
 * `/pos/customer-accounts` (Loyalty group). Storefront: the account area.
 *
 * Spec: docs/superpowers/specs/2026-10-06-customer-accounts-design.md
 *
 * Everything here is additive. `onlineAccess` is never trusted raw — always read
 * it through `deriveOnlineAccess`, so customer rows written before this feature
 * (no field at all) still resolve correctly.
 */
const zod_1 = require("zod");
// ── Online access ────────────────────────────────────────────────────────────
exports.CustomerOnlineAccessSchema = zod_1.z.enum([
    'none', // has a customer record, no online sign-in
    'invited', // invite email sent, not signed in yet
    'active', // signed in at least once
    'pending_approval', // signed up online; store must approve (onlineSignup = 'approval')
    'disabled', // store turned online access off for this customer
]);
exports.CustomerAccountSourceSchema = zod_1.z.enum(['till', 'online_signup', 'online_checkout', 'import', 'staff']);
/**
 * The customer's online-access status. A stored value wins; rows written before
 * Customer Accounts fall back to: a Cognito link → 'active', an invite stamp →
 * 'invited', otherwise 'none'.
 */
function deriveOnlineAccess(c) {
    if (!c)
        return 'none';
    const stored = exports.CustomerOnlineAccessSchema.safeParse(c.onlineAccess);
    if (stored.success)
        return stored.data;
    if (c.cognitoUserId || c.cognito_user_id)
        return 'active';
    if (c.invitedAt)
        return 'invited';
    return 'none';
}
// ── Settings (one per tenant, APosSettings settingId CUSTOMER_ACCOUNTS) ───────
exports.CustomerAccountsModeSchema = zod_1.z.enum(['loyaltyOnly', 'accountsOnly', 'both']);
exports.CustomerAccountsPresetSchema = zod_1.z.enum(['cafe', 'retail', 'regulated', 'services', 'wholesale']);
exports.CUSTOMER_ACCOUNT_SECTION_IDS = [
    'profile', 'addresses', 'payment', 'orders', 'rewards', 'store_credit',
    'limits', 'verification', 'appointments', 'client_files', 'trade_credit', 'residence',
];
exports.CustomerAccountSectionIdSchema = zod_1.z.enum(exports.CUSTOMER_ACCOUNT_SECTION_IDS);
exports.CustomerAccountSettingsSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(false),
    mode: exports.CustomerAccountsModeSchema.default('both'),
    preset: exports.CustomerAccountsPresetSchema.optional(),
    requireAt: zod_1.z.object({
        till: zod_1.z.enum(['never', 'restricted', 'always']).default('never'),
        tillCategoryIds: zod_1.z.array(zod_1.z.string().min(1).max(100)).max(200).default([]),
        checkout: zod_1.z.enum(['guest', 'required']).default('guest'),
        browse: zod_1.z.enum(['open', 'login', 'approved']).default('open'),
    }).default({}),
    /** Loyalty members who join at the till: email them an invite automatically? */
    newMemberInvite: zod_1.z.enum(['none', 'auto']).default('none'),
    /** Online sign-ups: open, or held as pending until the store approves. */
    onlineSignup: zod_1.z.enum(['open', 'approval']).default('open'),
    /** Merchant may HIDE an entitled section. Missing key = shown. Never grants a section. */
    sections: zod_1.z.record(exports.CustomerAccountSectionIdSchema, zod_1.z.boolean()).default({}),
});
/** Defaults for a tenant that has never saved settings. */
function defaultCustomerAccountSettings() {
    return exports.CustomerAccountSettingsSchema.parse({});
}
/** Business-type presets — UI shortcuts; the server stores the resolved values. */
exports.CUSTOMER_ACCOUNT_PRESETS = {
    cafe: { requireAt: { till: 'never', checkout: 'guest', browse: 'open' }, newMemberInvite: 'auto', onlineSignup: 'open' },
    retail: { requireAt: { till: 'never', checkout: 'guest', browse: 'open' }, newMemberInvite: 'auto', onlineSignup: 'open' },
    regulated: { requireAt: { till: 'restricted', checkout: 'required', browse: 'open' }, newMemberInvite: 'auto', onlineSignup: 'open' },
    services: { requireAt: { till: 'never', checkout: 'required', browse: 'open' }, newMemberInvite: 'auto', onlineSignup: 'open' },
    wholesale: { requireAt: { till: 'always', checkout: 'required', browse: 'approved' }, newMemberInvite: 'none', onlineSignup: 'approval' },
};
const PORTAL = 'pos.customers.portal';
exports.CUSTOMER_ACCOUNT_SECTIONS = [
    { id: 'rewards', label: 'Rewards card & points', description: 'Rewards number with a QR code to scan at the till', anyFeature: ['pos.customers.loyalty'], loyaltyOnly: true, status: 'live', packageLabel: 'Ruby Loyalty' },
    { id: 'orders', label: 'Order history', description: 'In-store receipts and online orders together', anyFeature: [PORTAL], status: 'live', packageLabel: 'Ruby Loyalty' },
    { id: 'profile', label: 'Profile & birthday', description: 'Name, phone, birthday and contact preferences', anyFeature: [PORTAL], status: 'live', packageLabel: 'Ruby Loyalty' },
    { id: 'addresses', label: 'Addresses', description: 'Saved delivery addresses', anyFeature: [PORTAL], status: 'live', packageLabel: 'Ruby Loyalty' },
    { id: 'payment', label: 'Payment', description: 'Saved payment methods', anyFeature: [PORTAL], status: 'live', packageLabel: 'Ruby Loyalty' },
    { id: 'store_credit', label: 'Store credit & gift cards', description: 'Balance and history', anyFeature: ['pos.store-credit'], status: 'live', packageLabel: 'Store Credit' },
    { id: 'limits', label: 'Buying limits', description: '"3 of 5 in the last 7 days" — in store and online', anyFeature: ['pos.compliance.cross-channel-limits', 'pos.compliance.purchase-limits', 'pos.compliance.age-verify'], status: 'live', packageLabel: 'Compliance' },
    { id: 'verification', label: 'Verification & documents', description: 'Age/ID check, expiry dates, uploads', anyFeature: ['pos.compliance.online-age-verify', 'pos.compliance.online-id-upload'], status: 'soon', packageLabel: 'Compliance' },
    { id: 'appointments', label: 'Appointments', description: 'Upcoming visits, reschedule, cancel', anyFeature: ['pos.services.appointments'], status: 'soon', packageLabel: 'Services' },
    { id: 'client_files', label: 'Client files & to-do', description: 'Forms, uploads, quotes, messages', anyFeature: ['pos.client-services'], status: 'planned', packageLabel: 'Client Services' },
    { id: 'trade_credit', label: 'Credit & balance (trade)', description: 'Credit limit, Net 30 terms, statements', anyFeature: ['pos.b2b.net-terms'], status: 'planned', packageLabel: 'Wholesale' },
    // Gated on estate.units (not estate.core): the resident record this page reads lives
    // behind Rental Unit Management. resident/me returns per-feature flags for
    // leasing/maintenance/autopay separately.
    { id: 'residence', label: 'My Residence', description: 'Your lease, rent and repair requests', anyFeature: ['estate.units'], status: 'live', packageLabel: 'Ruby Estate', line: 'rubyestate' },
];
/**
 * Resolve every catalogue section for a tenant. `state === 'on'` is the ONLY
 * state a storefront may render. Order follows the catalogue.
 */
function resolveCustomerAccountSections(features, settings) {
    const have = new Set(features);
    return exports.CUSTOMER_ACCOUNT_SECTIONS.map((s) => {
        const base = { id: s.id, label: s.label, description: s.description, packageLabel: s.packageLabel };
        if (s.status !== 'live')
            return { ...base, state: s.status };
        // A Ruby Estate area key (`estate.units`) is also met by any granular
        // feature inside it (`estate.units.resident-portal`).
        const met = (f) => have.has(f) || (/^estate\.[a-z]+$/.test(f) && [...have].some((h) => h.startsWith(`${f}.`)));
        if (!s.anyFeature.some(met))
            return { ...base, state: 'not_on_plan' };
        if (s.loyaltyOnly && settings.mode === 'accountsOnly')
            return { ...base, state: 'hidden' };
        if (settings.sections?.[s.id] === false)
            return { ...base, state: 'hidden' };
        return { ...base, state: 'on' };
    });
}
/**
 * Final review (B-I2): drops the sections that belong to another product line, so a Ruby Till
 * merchant's Customer Accounts settings never list "My Residence · Ruby Estate". Missing/unknown
 * line ⇒ Ruby Till. Works on resolved rows or catalogue defs (anything with an `id`).
 */
function customerAccountSectionsForLine(sections, productLine) {
    const line = productLine === 'rubyestate' ? 'rubyestate' : 'rubytill';
    const lineOf = new Map(exports.CUSTOMER_ACCOUNT_SECTIONS.map(s => [s.id, s.line]));
    return sections.filter(s => { const l = lineOf.get(s.id); return !l || l === line; });
}
// ── Rewards number ───────────────────────────────────────────────────────────
/** Crockford-style alphabet: no I, L, O, U — never confused when read aloud. */
exports.REWARDS_NUMBER_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** Random `XXXX-XXXX` rewards number. Random, so it never reveals customer counts. */
function generateRewardsNumber(rand = Math.random) {
    let out = '';
    for (let i = 0; i < 8; i++) {
        out += exports.REWARDS_NUMBER_ALPHABET[Math.floor(rand() * exports.REWARDS_NUMBER_ALPHABET.length)];
        if (i === 3)
            out += '-';
    }
    return out;
}
/** Normalise typed/scanned input: uppercase, strip spaces, map look-alikes, re-insert the dash. */
function normalizeRewardsNumber(input) {
    const raw = String(input || '').toUpperCase().replace(/[\s-]/g, '')
        .replace(/[IL]/g, '1').replace(/O/g, '0');
    if (raw.length !== 8 || [...raw].some((ch) => !exports.REWARDS_NUMBER_ALPHABET.includes(ch)))
        return null;
    return `${raw.slice(0, 4)}-${raw.slice(4)}`;
}
//# sourceMappingURL=customerAccounts.js.map